import { lazy, Suspense } from 'react';
import { useComposerStore } from './store';

/** Props of {@link ComposerHost}. */
export interface ComposerHostProps {
  /**
   * Opens a draft as its own page, e.g. `navigate({ to: '/compose/$draftId' })`.
   * The route belongs to the shell, so the shell hands the navigation in;
   * without it the windows simply have no "pop out" button.
   */
  onPopOut?: (draftId: string) => void;
}

/**
 * The composers, and the messages still waiting to go out.
 *
 * The shell renders it once, above the routes, so moving between folders —
 * or to the settings screen and back — never unmounts a half-written message.
 *
 * It is deliberately almost empty: this module is in the first download, and
 * everything it can show is behind a dynamic import. The watcher (a request
 * every reload makes) arrives just after the first paint; the windows arrive
 * the first time somebody writes something; the editor arrives with them
 * (ADR 0006, bundle budget).
 */
const PendingSendWatcher = lazy(() =>
  import('./PendingSendWatcher').then((module) => ({ default: module.PendingSendWatcher })),
);

const ComposerDock = lazy(() =>
  import('./ComposerDock').then((module) => ({ default: module.ComposerDock })),
);

export function ComposerHost({ onPopOut }: ComposerHostProps) {
  const windowCount = useComposerStore((state) => state.windows.length);
  return (
    <>
      <Suspense fallback={null}>
        <PendingSendWatcher />
      </Suspense>
      {windowCount > 0 && (
        <Suspense fallback={null}>
          <ComposerDock {...(onPopOut ? { onPopOut } : {})} />
        </Suspense>
      )}
    </>
  );
}
